import React, { useState, useCallback } from 'react';
import { User } from 'firebase/auth';
import { Button, IconButton } from './ui';
import './WorkflowSubmission.css';

interface WorkflowSubmissionProps {
  user: User;
  onSuccess?: (driveUrl: string) => void;
  onError?: (error: string) => void;
}

interface FormData {
  workflowTitle: string;
  department: string;
  taskDescription: string;
  whenToUse: string;
  expectedOutputs: string;
  frequency: string;
  dataSources: string[];
  dataSourceOther: string;
}

const WorkflowSubmission: React.FC<WorkflowSubmissionProps> = ({ user, onSuccess, onError }) => {
  const [formData, setFormData] = useState<FormData>({
    workflowTitle: '',
    department: '',
    taskDescription: '',
    whenToUse: '',
    expectedOutputs: '',
    frequency: '',
    dataSources: [],
    dataSourceOther: '',
  });

  const [referenceFiles, setReferenceFiles] = useState<File[]>([]);
  const [assetFiles, setAssetFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [driveUrl, setDriveUrl] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDataSourceChange = (dataSource: string) => {
    setFormData(prev => {
      const newDataSources = prev.dataSources.includes(dataSource)
        ? prev.dataSources.filter(ds => ds !== dataSource)
        : [...prev.dataSources, dataSource];
      return { ...prev, dataSources: newDataSources };
    });
  };

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: 'references' | 'assets') => {
      const files = Array.from(e.target.files || []);
      if (type === 'references') {
        setReferenceFiles(prev => [...prev, ...files]);
      } else {
        setAssetFiles(prev => [...prev, ...files]);
      }
    },
    []
  );

  const removeFile = useCallback(
    (index: number, type: 'references' | 'assets') => {
      if (type === 'references') {
        setReferenceFiles(prev => prev.filter((_, i) => i !== index));
      } else {
        setAssetFiles(prev => prev.filter((_, i) => i !== index));
      }
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Validate required fields
      if (!formData.workflowTitle || !formData.department || !formData.taskDescription ||
          !formData.whenToUse || !formData.expectedOutputs || !formData.frequency) {
        throw new Error('Please fill in all required fields');
      }

      // Get Firebase ID token
      const idToken = await user.getIdToken();

      // Create FormData for multipart upload
      const formDataToSend = new FormData();
      formDataToSend.append('workflowTitle', formData.workflowTitle);
      formDataToSend.append('department', formData.department);
      formDataToSend.append('taskDescription', formData.taskDescription);
      formDataToSend.append('whenToUse', formData.whenToUse);
      formDataToSend.append('expectedOutputs', formData.expectedOutputs);
      formDataToSend.append('frequency', formData.frequency);
      formDataToSend.append('dataSources', formData.dataSources.join(','));
      if (formData.dataSourceOther) {
        formDataToSend.append('dataSourceOther', formData.dataSourceOther);
      }

      // Append files
      referenceFiles.forEach(file => {
        formDataToSend.append('references', file);
      });
      assetFiles.forEach(file => {
        formDataToSend.append('assets', file);
      });

      // Submit to backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/workflow/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
        body: formDataToSend,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit workflow');
      }

      // Success!
      setSubmitSuccess(true);
      setDriveUrl(result.driveUrl);
      if (onSuccess) {
        onSuccess(result.driveUrl);
      }

      // Reset form
      setFormData({
        workflowTitle: '',
        department: '',
        taskDescription: '',
        whenToUse: '',
        expectedOutputs: '',
        frequency: '',
        dataSources: [],
        dataSourceOther: '',
      });
      setReferenceFiles([]);
      setAssetFiles([]);

    } catch (error: any) {
      console.error('Error submitting workflow:', error);
      setSubmitError(error.message || 'An error occurred while submitting');
      if (onError) {
        onError(error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Calculate total file size
  const totalFileSize = [...referenceFiles, ...assetFiles].reduce((sum, file) => sum + file.size, 0);
  const maxTotalSize = 25 * 1024 * 1024; // 25MB total limit

  return (
    <div className="workflow-submission-container">
      <div className="workflow-submission-header">
        <h1>Request a New Workflow Skill</h1>
        <p className="workflow-submission-subtitle">
          A <strong>Skill</strong> is a specialized workflow that helps Culture Genius perform specific tasks for your department.
          Think of it as training Claude to be an expert in your repetitive tasks - like generating reports, analyzing data, or creating documents automatically.
        </p>

        {/* What is a Skill? Explainer */}
        <div className="workflow-explainer-box">
          <h3>💡 What is a Skill?</h3>
          <p>
            Skills are pre-built workflows that make Culture Genius better at specific tasks. For example:
          </p>
          <ul>
            <li><strong>Revenue Analytics Skill</strong> - Automatically calculates revenue, CPM, and ROI from campaign data</li>
            <li><strong>Ad Ops Skill</strong> - Checks campaign pacing and generates weekly reports from Google Ad Manager</li>
            <li><strong>Creator Research Skill</strong> - Finds creators in Airtable based on demographics and follower counts</li>
          </ul>
          <p>
            <strong>Your submission will help us build a custom Skill for your workflow.</strong> The more detail you provide,
            the better we can automate your tasks!
          </p>
        </div>
      </div>

      {submitSuccess && driveUrl && (
        <div className="workflow-success-message">
          <h3>✓ Submission Successful!</h3>
          <p>Your workflow request has been saved to Google Drive.</p>
          <a href={driveUrl} target="_blank" rel="noopener noreferrer" className="workflow-drive-link">
            View Submission in Google Drive →
          </a>
        </div>
      )}

      {submitError && (
        <div className="workflow-error-message">
          <h3>✗ Submission Failed</h3>
          <p>{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="workflow-submission-form">
        {/* Submitter Info */}
        <section className="workflow-form-section">
          <h2>Submitter Information</h2>
          <div className="workflow-form-row">
            <div className="workflow-form-field">
              <label>Name</label>
              <input
                type="text"
                value={user.displayName || user.email?.split('@')[0] || 'User'}
                disabled
                className="workflow-input-disabled"
              />
            </div>
            <div className="workflow-form-field">
              <label>Email</label>
              <input
                type="email"
                value={user.email || user.providerData?.[0]?.email || ''}
                disabled
                className="workflow-input-disabled"
              />
            </div>
          </div>
        </section>

        {/* Workflow Details */}
        <section className="workflow-form-section">
          <h2>Workflow Details</h2>

          <div className="workflow-form-field">
            <label htmlFor="workflowTitle">Workflow Title *</label>
            <input
              type="text"
              id="workflowTitle"
              name="workflowTitle"
              value={formData.workflowTitle}
              onChange={handleInputChange}
              placeholder="e.g., Weekly Revenue Report Generation"
              required
              className="workflow-input"
            />
            <p className="workflow-field-hint">
              <strong>What makes a good title?</strong> Be specific and action-oriented.
              Examples: "Monthly Creator Performance Report", "Campaign Pacing Alert System", "New Creator Onboarding Checklist"
            </p>
          </div>

          <div className="workflow-form-field">
            <label htmlFor="department">Department *</label>
            <select
              id="department"
              name="department"
              value={formData.department}
              onChange={handleInputChange}
              required
              className="workflow-select"
            >
              <option value="">Select a department...</option>
              <option value="Finance & Analytics">Finance & Analytics</option>
              <option value="Ad Operations">Ad Operations</option>
              <option value="Creator Research">Creator Research</option>
              <option value="Content Strategy">Content Strategy</option>
              <option value="Sales">Sales</option>
              <option value="Marketing">Marketing</option>
              <option value="Engineering">Engineering</option>
              <option value="Other">Other</option>
            </select>
            <p className="workflow-field-hint">
              <strong>Why does this matter?</strong> Skills are organized by department to make them easier to find and use.
              This also helps us understand the context and data you typically work with.
            </p>
          </div>

          <div className="workflow-form-field">
            <label htmlFor="taskDescription">Task Description *</label>
            <textarea
              id="taskDescription"
              name="taskDescription"
              value={formData.taskDescription}
              onChange={handleInputChange}
              placeholder="Example: Every week I need to pull revenue data from Google Ad Manager for all active campaigns, calculate the total earnings, subtract platform fees, determine each creator's share, and create a summary report showing top performers and any underperforming channels that need attention."
              required
              rows={6}
              className="workflow-textarea"
            />
            <p className="workflow-field-hint">
              <strong>Be specific about the steps!</strong> Good: "Pull campaign data from Ad Manager, filter by date range, calculate pacing percentage, flag campaigns over 20% behind schedule."
              Bad: "Check campaign pacing." The more detail you provide about WHAT you do and HOW you do it, the better the Skill will work.
            </p>
          </div>

          <div className="workflow-form-field">
            <label htmlFor="whenToUse">When to Use This Workflow *</label>
            <textarea
              id="whenToUse"
              name="whenToUse"
              value={formData.whenToUse}
              onChange={handleInputChange}
              placeholder="Example: Use this every Monday at 9am to review last week's performance. Also use when a sales team member asks for quick creator stats, or when preparing for quarterly business reviews."
              required
              rows={4}
              className="workflow-textarea"
            />
            <p className="workflow-field-hint">
              <strong>Think about triggers and timing.</strong> When would someone ask Culture Genius to run this workflow?
              Is it scheduled (daily/weekly)? Event-driven (when a campaign ends)? On-demand (when boss asks for data)?
              This helps us understand how to prompt Claude to use this Skill.
            </p>
          </div>

          <div className="workflow-form-field">
            <label htmlFor="expectedOutputs">Expected Outputs *</label>
            <textarea
              id="expectedOutputs"
              name="expectedOutputs"
              value={formData.expectedOutputs}
              onChange={handleInputChange}
              placeholder="Example: A professional PDF report with: 1) Executive summary of total revenue, 2) Table showing top 10 creators by earnings, 3) Bar chart comparing this month vs last month, 4) List of any creators who need attention. Report should use Culture Genesis branding and be ready to share with leadership."
              required
              rows={4}
              className="workflow-textarea"
            />
            <p className="workflow-field-hint">
              <strong>What should the final result look like?</strong> Be specific about format (PDF, Google Doc, CSV, email summary),
              content (tables, charts, bullet points), and quality (professional formatting, branding, ready to share with clients).
              If you have an example of what you want, upload it in the "Assets" section below!
            </p>
          </div>

          <div className="workflow-form-field">
            <label htmlFor="frequency">Frequency *</label>
            <select
              id="frequency"
              name="frequency"
              value={formData.frequency}
              onChange={handleInputChange}
              required
              className="workflow-select"
            >
              <option value="">Select frequency...</option>
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Ad-hoc">Ad-hoc / As Needed</option>
            </select>
            <p className="workflow-field-hint">
              <strong>How often will you use this?</strong> This helps us prioritize which Skills to build first and optimize for performance.
              Frequent workflows (daily/weekly) get higher priority than occasional tasks.
            </p>
          </div>
        </section>

        {/* Data Sources */}
        <section className="workflow-form-section">
          <h2>Data Sources</h2>
          <p className="workflow-section-description">
            <strong>Where does the data come from?</strong> Select all data sources your workflow needs.
            These are already connected to Culture Genius, so the Skill can access them automatically.
          </p>

          <div className="workflow-checkbox-group">
            <label className="workflow-checkbox-label">
              <input
                type="checkbox"
                checked={formData.dataSources.includes('airtable')}
                onChange={() => handleDataSourceChange('airtable')}
              />
              <div className="workflow-checkbox-content">
                <strong>Airtable (CultureGen Master Creator List)</strong>
                <p className="workflow-checkbox-description">
                  Creator database with demographics, follower counts, content types, and performance metrics.
                  Use for: Finding creators, filtering by demographics, pulling channel data.
                </p>
              </div>
            </label>

            <label className="workflow-checkbox-label">
              <input
                type="checkbox"
                checked={formData.dataSources.includes('googleDrive')}
                onChange={() => handleDataSourceChange('googleDrive')}
              />
              <div className="workflow-checkbox-content">
                <strong>Google Drive</strong>
                <p className="workflow-checkbox-description">
                  Search and read files, create new documents/PDFs/spreadsheets, save reports to specific folders.
                  Use for: Accessing existing documents, creating reports, storing outputs.
                </p>
              </div>
            </label>

            <label className="workflow-checkbox-label">
              <input
                type="checkbox"
                checked={formData.dataSources.includes('gmail')}
                onChange={() => handleDataSourceChange('gmail')}
              />
              <div className="workflow-checkbox-content">
                <strong>Gmail</strong>
                <p className="workflow-checkbox-description">
                  Search emails, read messages, extract information from correspondence.
                  Use for: Finding client requests, pulling data from email threads, reviewing communications.
                </p>
              </div>
            </label>

            <label className="workflow-checkbox-label">
              <input
                type="checkbox"
                checked={formData.dataSources.includes('googleAdManager')}
                onChange={() => handleDataSourceChange('googleAdManager')}
              />
              <div className="workflow-checkbox-content">
                <strong>Google Ad Manager</strong>
                <p className="workflow-checkbox-description">
                  Campaign data, line item pacing, targeting configs, delivery forecasts, orders by date.
                  Use for: Campaign performance reports, pacing alerts, ad ops workflows.
                </p>
              </div>
            </label>

            <label className="workflow-checkbox-label">
              <input
                type="checkbox"
                checked={formData.dataSources.includes('youtubeStudio')}
                onChange={() => handleDataSourceChange('youtubeStudio')}
              />
              <div className="workflow-checkbox-content">
                <strong>YouTube Studio</strong>
                <p className="workflow-checkbox-description">
                  Analytics, view counts, subscriber growth, performance metrics for our channels.
                  Use for: Creator performance analysis, content strategy, monthly reports.
                </p>
              </div>
            </label>

            <label className="workflow-checkbox-label">
              <input
                type="checkbox"
                checked={formData.dataSources.includes('webSearch')}
                onChange={() => handleDataSourceChange('webSearch')}
              />
              <div className="workflow-checkbox-content">
                <strong>Web Search</strong>
                <p className="workflow-checkbox-description">
                  Real-time internet search for current information beyond Culture Genesis data.
                  Use for: Researching creators, competitive analysis, finding social media profiles.
                </p>
              </div>
            </label>
          </div>

          <div className="workflow-form-field">
            <label htmlFor="dataSourceOther">Other Data Source (Optional)</label>
            <input
              type="text"
              id="dataSourceOther"
              name="dataSourceOther"
              value={formData.dataSourceOther}
              onChange={handleInputChange}
              placeholder="e.g., Salesforce (for sales data), Slack (for team communications), LinkedIn (for creator research)"
              className="workflow-input"
            />
            <p className="workflow-field-hint">
              <strong>Need a data source we don't have yet?</strong> Describe what it is and what data you need from it.
              We'll evaluate adding it to Culture Genius if it's useful for multiple workflows.
            </p>
          </div>
        </section>

        {/* Reference Documents */}
        <section className="workflow-form-section">
          <h2>Reference Documents</h2>
          <p className="workflow-section-description">
            <strong>Help us understand your workflow better!</strong> Upload examples, documentation, or any files that show what you currently do.
            These help us build a Skill that matches your exact process.
          </p>
          <div className="workflow-explainer-box-small">
            <p><strong>📄 What makes a good reference document?</strong></p>
            <ul>
              <li><strong>Example reports:</strong> PDF/Excel files showing what your final output looks like</li>
              <li><strong>Email examples:</strong> Screenshots or PDFs of typical requests you receive</li>
              <li><strong>Process documentation:</strong> Step-by-step guides you currently follow</li>
              <li><strong>Data samples:</strong> CSV/Excel with typical data format (anonymized if needed)</li>
              <li><strong>Client requirements:</strong> Documents explaining what stakeholders expect</li>
            </ul>
            <p><em>💡 Tip: The more context you provide, the better the Skill will work!</em></p>
          </div>

          <div className="workflow-file-upload">
            <label htmlFor="referenceFiles" className="workflow-file-upload-label">
              <span className="workflow-file-upload-icon">📄</span>
              <span>Choose Reference Files</span>
              <input
                type="file"
                id="referenceFiles"
                multiple
                onChange={(e) => handleFileChange(e, 'references')}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.html,.json"
                className="workflow-file-input"
              />
            </label>

            {referenceFiles.length > 0 && (
              <ul className="workflow-file-list">
                {referenceFiles.map((file, index) => (
                  <li key={index} className="workflow-file-item">
                    <span className="workflow-file-name">{file.name}</span>
                    <span className="workflow-file-size">{formatFileSize(file.size)}</span>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      label="Remove file"
                      onClick={() => removeFile(index, 'references')}
                    >
                      ✕
                    </IconButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Assets */}
        <section className="workflow-form-section">
          <h2>Assets</h2>
          <p className="workflow-section-description">
            <strong>What should be included in the output?</strong> Upload templates, branding materials, or any assets
            that the Skill should use when creating documents or reports.
          </p>
          <div className="workflow-explainer-box-small">
            <p><strong>🎨 What are assets?</strong></p>
            <ul>
              <li><strong>Report templates:</strong> HTML/Word templates with Culture Genesis branding</li>
              <li><strong>Logos and images:</strong> Company logo, header images for reports</li>
              <li><strong>Style guides:</strong> Brand colors, fonts, formatting requirements</li>
              <li><strong>Email templates:</strong> Standard formats for client communications</li>
              <li><strong>Charts/graphics:</strong> Visual elements that should appear in reports</li>
            </ul>
            <p><em>💡 Example: If the Skill creates weekly reports, upload your branded PowerPoint template so outputs match your style!</em></p>
          </div>

          <div className="workflow-file-upload">
            <label htmlFor="assetFiles" className="workflow-file-upload-label">
              <span className="workflow-file-upload-icon">🎨</span>
              <span>Choose Asset Files</span>
              <input
                type="file"
                id="assetFiles"
                multiple
                onChange={(e) => handleFileChange(e, 'assets')}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.svg,.html,.css"
                className="workflow-file-input"
              />
            </label>

            {assetFiles.length > 0 && (
              <ul className="workflow-file-list">
                {assetFiles.map((file, index) => (
                  <li key={index} className="workflow-file-item">
                    <span className="workflow-file-name">{file.name}</span>
                    <span className="workflow-file-size">{formatFileSize(file.size)}</span>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      label="Remove file"
                      onClick={() => removeFile(index, 'assets')}
                    >
                      ✕
                    </IconButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* File Size Warning */}
        {totalFileSize > maxTotalSize && (
          <div className="workflow-error-message">
            <h3>⚠ File Size Limit Exceeded</h3>
            <p>
              Total file size is {formatFileSize(totalFileSize)}, but the maximum is {formatFileSize(maxTotalSize)}.
              Please remove some files or compress them before submitting.
            </p>
          </div>
        )}

        {/* Submit Button */}
        <div className="workflow-form-actions">
          <div className="workflow-file-size-info">
            Total file size: {formatFileSize(totalFileSize)} / {formatFileSize(maxTotalSize)}
            {totalFileSize > 0 && totalFileSize <= maxTotalSize && ' ✓'}
          </div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={isSubmitting || totalFileSize > maxTotalSize}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Workflow Request'}
          </Button>
          <p className="workflow-submit-note">
            Your submission will be saved to Google Drive and reviewed by the Culture Genius team.
            We'll reach out if we need more information!
          </p>
        </div>
      </form>
    </div>
  );
};

export default WorkflowSubmission;
